import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    effect,
    inject,
    input,
    OnDestroy,
    OnInit,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
    catchError,
    debounceTime,
    EMPTY,
    forkJoin,
    Observable,
    of,
    Subject,
    Subscription,
    switchMap,
} from 'rxjs';
import {
    CoverTemplateEntity,
    CoverTemplateFieldPartial,
    DEFAULT_COVER_TEMPLATE_NAME,
} from 'src/app/domain/entities/partials/cover-template/cover-template.entity';
import {
    DocumentCoverItemValue,
    DocumentCoverPartial,
} from 'src/app/domain/entities/partials/documents/document-cover.partial';
import { GenericDictionaryFieldPartial } from 'src/app/domain/entities/partials/generics/generic-dictionary-field.partial';
import { GenericImageFieldPartial } from 'src/app/domain/entities/partials/generics/generic-image-field.partial';
import { GenericTextFieldPartial } from 'src/app/domain/entities/partials/generics/generic-text-field.partial';
import { GenericItemTypeEnum } from 'src/app/domain/enums/generic-item-type.enum';
import { RaLoadingComponent } from '@shared/components';
import { RaSwitchComponent } from 'ra-ui';
import { RaButtonComponent } from 'src/app/shared/components/ra-button/ra-button.component';
import { DATA_TAG_CONTEXT } from 'src/app/shared/components/ra-editor/data-tag-context.token';
import { getDataTagLabel } from 'src/app/shared/components/ra-editor/data-tag-i18n';
import { resolveDataTagValue } from 'src/app/shared/components/ra-editor/data-tag-value.resolver';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { RaModalComponent } from 'src/app/shared/components/ra-modal/ra-modal.component';
import {
    LinkTokenComposerValue,
    RaLinkTokenComposerComponent,
} from 'src/app/shared/components/ra-link-token-composer/ra-link-token-composer.component';
import { sanitizeGenericFields } from 'src/app/shared/utils/generic-fields.util';
import { MediaUtils } from 'src/app/shared/utils/media.utils';
import { CoverFragmentBuilderService } from '../../../../pdf-preview/cover-fragment-builder.service';
import { EditDataService } from '../../../services/_edit-data.service';
import { EditService } from '../../../services/edit-service';

export interface CoverEditorSaveEvent {
    cover: DocumentCoverPartial;
}

/** Cores de fundo sugeridas (presets) para a capa. */
const PRESET_COLORS = ['#1F1F1F', '#2F5BEA', '#0F6E56', '#993C1D', '#6D28D9', '#B91C1C', '#0C447C'];
const COMPANY_LOGO_TOKEN = '$COMPANY_LOGO';
const COMPANY_LOGO_TAG = 'link_company_logo';
/** Grupo "Gerais": itens com esta tag OU sem tagGroup; sempre a 1ª aba (espelha o app). */
const GENERAL_GROUP = 'cover_template_group_general';

@Component({
    selector: 'modal-cover-editor',
    templateUrl: 'modal-cover-editor.component.html',
    styleUrl: 'modal-cover-editor.component.scss',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslatePipe,
        RaModalComponent,
        RaButtonComponent,
        RaSwitchComponent,
        RaIconComponent,
        RaLoadingComponent,
        RaLinkTokenComposerComponent,
    ],
})
export class ModalCoverEditorComponent implements OnInit, OnDestroy {
    private _translate = inject(TranslateService);
    private _editService = inject(EditService);
    private _editDataService = inject(EditDataService);
    private _media = inject(MediaUtils);
    private _dataTagContext = inject(DATA_TAG_CONTEXT, { optional: true });
    private _coverFragmentBuilder = inject(CoverFragmentBuilderService);
    private _sanitizer = inject(DomSanitizer);

    public loading = input<boolean>(false);
    public close = output<void>();
    public save = output<CoverEditorSaveEvent>();

    /**
     * Overrides opcionais para reusar o editor fora do documento (order/company).
     * Quando ausentes, usa o caminho document-scoped do `EditService`/`EditDataService`
     * — assim o fluxo do documento fica intacto.
     */
    public templatesLoaderFn = input<(() => Observable<CoverTemplateEntity[]>) | null>(null);
    public currentTemplateLoaderFn = input<(() => Observable<CoverTemplateEntity | null>) | null>(
        null
    );
    public templateChangerFn = input<
        ((templateId: string) => Observable<CoverTemplateEntity | null>) | null
    >(null);
    public imageUploaderFn = input<((file: File) => Observable<string>) | null>(null);
    /** Nome da capa atual (fallback ao semear) — substitui a leitura do documento. */
    public currentCoverName = input<string | null>(null);

    public readonly TEXT = GenericItemTypeEnum.TEXT;
    public readonly IMAGE = GenericItemTypeEnum.IMAGE;
    public readonly DICTIONARY = GenericItemTypeEnum.DICTIONARY;
    public readonly presetColors = PRESET_COLORS;

    public loadingData = signal(true);
    public switchingTemplate = signal(false);
    public logoUploadingId = signal<string | null>(null);

    /** Working copy (CoverTemplateEntity) — fonte do preview e da edição. */
    public working = signal<CoverTemplateEntity | null>(null);
    public templates = signal<CoverTemplateEntity[]>([]);
    public visibilityControls = new Map<string, FormControl<boolean>>();
    public activeGroup = signal<string>(GENERAL_GROUP);

    /** Itens editáveis. */
    public editableItems = computed(() =>
        (this.working()?.items ?? []).filter(i => i.canEdit !== false)
    );

    /** Grupo de um item: "Gerais" se sem tagGroup ou se for a tag de gerais. */
    private _groupKey(item: CoverTemplateFieldPartial): string {
        const g = item.tagGroup?.trim();
        return !g || g === GENERAL_GROUP ? GENERAL_GROUP : g;
    }

    /** Abas: "Gerais" sempre primeiro (e sempre presente — abriga o card de cor), demais na ordem. */
    public groups = computed(() => {
        const list: string[] = [GENERAL_GROUP];
        for (const item of this.editableItems()) {
            const g = this._groupKey(item);
            if (g !== GENERAL_GROUP && !list.includes(g)) list.push(g);
        }
        return list;
    });

    /** Aba "Gerais" ativa → mostra o card de cor de fundo no topo. */
    public isGeneralTab = computed(() => this.activeGroup() === GENERAL_GROUP);

    public itemsOfActiveGroup = computed(() => {
        const g = this.activeGroup();
        return this.editableItems().filter(i => this._groupKey(i) === g);
    });

    public backgroundColor = computed(() => this.working()?.background?.colors?.[0]?.color ?? '');

    /**
     * Cópia do template só para o preview (WYSIWYG com a capa aplicada/PDF): campos de
     * dicionário visíveis e vazios viram `-` em vez de sumirem — assim o espaçamento fica
     * consistente entre modelos e igual ao resultado final. Não afeta o que é salvo.
     */
    public previewTemplate = computed<CoverTemplateEntity | null>(() => {
        const tpl = this.working();
        if (!tpl) return tpl;
        return {
            ...tpl,
            items: (tpl.items ?? []).map(it => {
                if (it.type !== GenericItemTypeEnum.DICTIONARY || it.isVisible === false) return it;
                const v = it.value as GenericDictionaryFieldPartial | undefined;
                // `value` guarda o TOKEN (`$CLIENT_EMAIL`) quando o campo é um link; quem
                // diz se há dado é o `linkValueConverted` resolvido pela API.
                const resolved = v?.linkValueConverted ?? (v?.value?.startsWith('$') ? '' : v?.value);
                if (resolved) return it;
                const value: GenericDictionaryFieldPartial = {
                    ...(v ?? { key: '', value: '' }),
                    linkValueConverted: '-',
                };
                return { ...it, value };
            }),
        };
    });

    /** URL da logo da empresa (resolve o token $COMPANY_LOGO no preview). */
    public companyLogo = computed(() => this._dataTagContext?.()?.company?.logo ?? '');

    public mainModal = viewChild<RaModalComponent>('mainModal');

    // * [Prévia da capa]
    // A folha é montada AQUI, no navegador, pelo mesmo `cover-renderer.ts` que a API usa para
    // imprimir o PDF (`CoverFragmentBuilderService`) — o mesmo caminho que o painel de prévia
    // do editor já usa para o documento inteiro. Sem ida ao servidor, então a folha acompanha
    // a digitação: o debounce curto existe só para não remontar a cada tecla.
    public pdfPreviewDoc = signal<SafeHtml | null>(null);

    private _pdfPreview$ = new Subject<CoverTemplateEntity>();
    private _pdfPreviewSub: Subscription;

    constructor() {
        this._pdfPreviewSub = this._pdfPreview$
            .pipe(
                debounceTime(150),
                // switchMap: o build é assíncrono (o fundo SVG pode ir buscar), e sem o
                // cancelamento uma montagem antiga podia chegar depois da nova.
                switchMap(tpl => this._coverFragmentBuilder.buildPage(tpl).catch(() => null))
            )
            .subscribe(html => {
                // Falhou: a folha anterior fica, que é melhor do que piscar para o vazio.
                if (html === null) return;
                this.pdfPreviewDoc.set(this._sanitizer.bypassSecurityTrustHtml(html));
            });

        effect(() => {
            const tpl = this.previewTemplate();
            if (!tpl) return;
            this._pdfPreview$.next(tpl);
        });
    }

    ngOnDestroy(): void {
        this._pdfPreviewSub.unsubscribe();
        this._pdfPreview$.complete();
    }

    ngOnInit(): void {
        // Busca o template atual do doc (getById) e a lista (getAll) juntos. O getById pode
        // vir null (doc sem capa) — nesse caso semeia a partir da lista, deixando o usuário
        // escolher/criar um modelo (o `change-cover-template` grava no doc ao selecionar).
        const currentLoader =
            this.currentTemplateLoaderFn() ?? (() => this._editService.getCoverTemplate());
        const templatesLoader =
            this.templatesLoaderFn() ?? (() => this._editService.getAllCoverTemplates());

        forkJoin({
            current: currentLoader().pipe(catchError(() => of(null))),
            all: templatesLoader().pipe(catchError(() => of([] as CoverTemplateEntity[]))),
        }).subscribe({
            next: ({ current, all }) => {
                const list = this._sortByLabel(all ?? []);
                this.templates.set(list);
                this._seed(current ?? this._fallbackTemplate(list));
                this.loadingData.set(false);
            },
            error: () => this.loadingData.set(false),
        });
    }

    /**
     * Ordem alfabética pelo rótulo que a galeria desenha, e não pelo nome no banco: o que o
     * usuário lê é a tag traduzida, então a ordem muda com o idioma. `localeCompare` para
     * acento não jogar a palavra para o fim da lista.
     */
    private _sortByLabel(list: CoverTemplateEntity[]): CoverTemplateEntity[] {
        const language = this._translate.currentLang || this._translate.defaultLang || undefined;

        return [...list].sort((a, b) =>
            this.templateLabel(a).localeCompare(this.templateLabel(b), language, {
                sensitivity: 'base',
            }),
        );
    }

    /** Modelo de partida quando o doc ainda não tem capa: o do nome salvo, ou o padrão. */
    private _fallbackTemplate(list: CoverTemplateEntity[]): CoverTemplateEntity | null {
        if (!list.length) return null;
        const currentName =
            this.currentCoverName() ?? this._editDataService.document()?.cover?.name;

        // `list[0]` era o modelo padrão enquanto a lista vinha na ordem do banco; agora ela vem
        // em ordem alfabética, e o primeiro é só quem calhou de vir antes no alfabeto.
        return (
            list.find(t => t.name === currentName) ??
            list.find(t => t.name === DEFAULT_COVER_TEMPLATE_NAME) ??
            list[0]
        );
    }

    private _seed(tpl: CoverTemplateEntity | null): void {
        if (!tpl) return;
        // Clona o template e os itens/value para não mutar o original.
        const clone: CoverTemplateEntity = {
            ...tpl,
            background: tpl.background
                ? { ...tpl.background, colors: (tpl.background.colors ?? []).map(c => ({ ...c })) }
                : tpl.background,
            // O spread transformaria um item fora de forma (ex.: `[]` vindo do banco) num
            // `{}`, que o `confirmSave` devolveria pra API. Ver [sanitizeGenericFields].
            items: sanitizeGenericFields(tpl.items).map(it => ({
                ...it,
                value: it.value ? { ...it.value } : it.value,
            })),
        };
        this.working.set(clone);

        this.visibilityControls.clear();
        for (const item of clone.items ?? []) {
            if (!item.fieldId || !item.canHide) continue;
            const fieldId = item.fieldId;
            const ctrl = new FormControl<boolean>(item.isVisible !== false, { nonNullable: true });
            ctrl.valueChanges.subscribe(val => this._onToggle(fieldId, val));
            this.visibilityControls.set(fieldId, ctrl);
        }

        const groups = this.groups();
        if (!groups.includes(this.activeGroup())) this.activeGroup.set(groups[0] ?? GENERAL_GROUP);
    }

    public controlFor(fieldId?: string): FormControl<boolean> | null {
        return (fieldId && this.visibilityControls.get(fieldId)) || null;
    }

    // * [Templates]
    public isSelectedTemplate(tpl: CoverTemplateEntity): boolean {
        return this.working()?.name === tpl.name;
    }

    /**
     * Rótulo do modelo. A tag do banco (`cover_template_01_model_name`) é a primeira escolha,
     * mas ela só existe nos dicionários que vieram neste deploy: um modelo criado depois cai
     * no `modelName`, o rótulo em texto puro que o próprio documento do banco carrega.
     */
    public templateLabel(tpl: CoverTemplateEntity): string {
        const key = tpl.tagModelName?.trim();
        const translated = key ? this._translate.instant(key) : '';

        if (translated && translated !== key) return translated;

        return tpl.modelName?.trim() || tpl.name || '';
    }

    /**
     * Thumbnail do modelo: a do banco (CDN) quando existe, senão o SVG estático que veio no
     * deploy — que só cobre os modelos que existiam quando ele foi feito.
     */
    public templateImage(tpl: CoverTemplateEntity): string {
        const url = tpl.thumbnailUrl?.trim();
        if (url) return url;

        const num = (tpl.name ?? '').replace('TEMPLATE_', '');
        return `/cover-templates/template_${num}.svg`;
    }

    /** Modelos cujo thumbnail estático falhou — caem para um placeholder. */
    public failedThumbs = signal<Set<string>>(new Set());

    public hasThumb(tpl: CoverTemplateEntity): boolean {
        return !this.failedThumbs().has(tpl.name);
    }

    public onThumbError(tpl: CoverTemplateEntity): void {
        this.failedThumbs.update(s => new Set(s).add(tpl.name));
    }

    public selectTemplate(tpl: CoverTemplateEntity): void {
        if (this.isSelectedTemplate(tpl) || this.switchingTemplate() || !tpl._id) return;
        this.switchingTemplate.set(true);
        const changer =
            this.templateChangerFn() ?? ((id: string) => this._editService.changeCoverTemplate(id));
        changer(tpl._id).subscribe({
            next: newTpl => {
                this._seed(newTpl);
                this.switchingTemplate.set(false);
            },
            error: () => this.switchingTemplate.set(false),
        });
    }

    // * [Fundo]
    public isPresetSelected(color: string): boolean {
        return (this.backgroundColor() || '').toUpperCase() === color.toUpperCase();
    }

    public setBackgroundColor(color: string): void {
        this.working.update(tpl => {
            if (!tpl) return tpl;
            const bg = tpl.background ?? { value: '', colors: [] };
            const id = bg.colors?.[0]?.id ?? 'rapidocs-color';
            return { ...tpl, background: { ...bg, colors: [{ id, color }] } };
        });
    }

    // * [Itens]
    private _onToggle(fieldId: string, val: boolean): void {
        this.working.update(tpl => {
            if (!tpl) return tpl;
            return {
                ...tpl,
                items: (tpl.items ?? []).map(i =>
                    i.fieldId === fieldId ? { ...i, isVisible: val } : i
                ),
            };
        });
    }

    public onComposerChange(item: CoverTemplateFieldPartial, value: LinkTokenComposerValue): void {
        const isDict = item.type === GenericItemTypeEnum.DICTIONARY;
        this.working.update(tpl => {
            if (!tpl) return tpl;
            return {
                ...tpl,
                items: (tpl.items ?? []).map(i => {
                    if (i.fieldId !== item.fieldId) return i;
                    const v: any = { ...(i.value ?? {}) };
                    if (value.link) {
                        const token =
                            value.link.value || this._tokenFromTagName(value.link.tagName);
                        if (isDict) v.value = token;
                        else v.text = token;
                        v.linkTagName = value.link.tagName;
                        // Resolve o token só para o preview ao vivo; é removido no Save (o backend re-resolve).
                        v.linkValueConverted = this._resolveDisplay(value.link.tagName);
                    } else {
                        if (isDict) v.value = value.text ?? '';
                        else v.text = value.text ?? '';
                        v.linkTagName = undefined;
                        v.linkValueConverted = undefined;
                    }
                    return { ...i, value: v };
                }),
            };
        });
    }

    // * [Logo / imagem]
    public logoUrl(item: CoverTemplateFieldPartial): string {
        const v = item.value as GenericImageFieldPartial | undefined;
        const url = v?.linkValueConverted || v?.url || '';
        return url.startsWith('$') ? '' : url;
    }

    public useCompanyLogo(item: CoverTemplateFieldPartial): void {
        // Persiste o token; resolve só para o preview (linkValueConverted é removido no Save).
        this._setImage(item.fieldId, COMPANY_LOGO_TOKEN, COMPANY_LOGO_TAG, this.companyLogo());
    }

    public uploadImage(item: CoverTemplateFieldPartial): void {
        if (this.logoUploadingId()) return;
        const fieldId = item.fieldId ?? '';
        this.logoUploadingId.set(fieldId);
        this._media
            .selectImagesFromGallery(1)
            .pipe(
                switchMap(files => {
                    const file = files[0];
                    if (!file) return EMPTY;
                    const uploader =
                        this.imageUploaderFn() ??
                        ((f: File) => this._editService.uploadCoverImage(f));
                    return uploader(file);
                })
            )
            .subscribe({
                next: url => {
                    this._setImage(fieldId, url);
                    this.logoUploadingId.set(null);
                },
                error: () => this.logoUploadingId.set(null),
                complete: () => this.logoUploadingId.set(null),
            });
    }

    private _setImage(
        fieldId: string | undefined,
        url: string,
        linkTagName?: string,
        linkValueConverted?: string
    ): void {
        if (!fieldId) return;
        this.working.update(tpl => {
            if (!tpl) return tpl;
            return {
                ...tpl,
                items: (tpl.items ?? []).map(i => {
                    if (i.fieldId !== fieldId) return i;
                    const v: GenericImageFieldPartial = {
                        ...(i.value as GenericImageFieldPartial),
                    };
                    v.url = url;
                    v.linkTagName = linkTagName;
                    v.linkValueConverted = linkValueConverted || undefined;
                    return { ...i, value: v };
                }),
            };
        });
    }

    public isCompanyLogoField(item: CoverTemplateFieldPartial): boolean {
        return (
            item.fieldId === 'company-logo' ||
            (item.value as GenericImageFieldPartial)?.linkTagName === COMPANY_LOGO_TAG
        );
    }

    // * [Helpers]
    public iconFor(item: CoverTemplateFieldPartial): string {
        if (item.type === GenericItemTypeEnum.IMAGE) return 'gallery';
        if (item.type === GenericItemTypeEnum.DICTIONARY) return 'document-copy';
        return 'text';
    }

    public titleFor(item: CoverTemplateFieldPartial): string {
        return item.tagTitle ? this._translate.instant(item.tagTitle) : (item.fieldId ?? '');
    }

    public descFor(item: CoverTemplateFieldPartial): string {
        return item.tagDescription ? this._translate.instant(item.tagDescription) : '';
    }

    public groupLabel(group: string): string {
        const translated = this._translate.instant(group);
        return translated === group ? group : translated;
    }

    public valueText(item: CoverTemplateFieldPartial): string {
        if (item.type === GenericItemTypeEnum.DICTIONARY) {
            return (item.value as GenericDictionaryFieldPartial)?.value ?? '';
        }
        return (item.value as GenericTextFieldPartial)?.text ?? '';
    }

    public valueLinkTag(item: CoverTemplateFieldPartial): string {
        return (
            (item.value as GenericTextFieldPartial | GenericDictionaryFieldPartial)?.linkTagName ??
            ''
        );
    }

    public limitCharOf(item: CoverTemplateFieldPartial): number | undefined {
        return (item.value as GenericTextFieldPartial | GenericDictionaryFieldPartial)?.limitChar;
    }

    public animateClose(): void {
        this.mainModal()?.handleClose();
    }

    public confirmSave(): void {
        const tpl = this.working();
        if (!tpl) return;
        const cover: DocumentCoverPartial = {
            name: tpl.name,
            coverTemplateItems: (tpl.items ?? []).map(i => {
                // Remove o linkValueConverted (resolvido só p/ preview) — o backend re-resolve no save.
                const value = i.value ? { ...i.value, linkValueConverted: undefined } : i.value;
                return {
                    fieldId: i.fieldId ?? '',
                    value,
                    isVisible: i.isVisible,
                } as DocumentCoverItemValue;
            }),
            coverBackgroundProperties: tpl.background?.colors ?? [],
        };
        // Ver comentário em ModalFooterEditorComponent.confirmSave.
        this.save.emit({ cover });
    }

    private _tokenFromTagName(tagName: string): string {
        return '$' + tagName.replace(/^link_/, '').toUpperCase();
    }

    /** Resolve um token para exibição no preview (valor do contexto ou rótulo i18n). */
    private _resolveDisplay(tagName: string): string {
        const ctx = this._dataTagContext?.() ?? null;
        const resolved = resolveDataTagValue(tagName, ctx);
        if (resolved != null && String(resolved).trim() !== '') return String(resolved);
        return getDataTagLabel(this._translate, tagName);
    }
}
